import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Dumbbell, Salad, MessageSquare, Camera, ClipboardList, Send, Pencil } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import CoachShell from '@/components/CoachShell';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';

const TABS = [
  { key: 'master', icon: ClipboardList, tk: 'coach.master' },
  { key: 'training', icon: Dumbbell, tk: 'coach.trainingPlan' },
  { key: 'nutrition', icon: Salad, tk: 'coach.nutritionPlan' },
  { key: 'checkins', icon: MessageSquare, tk: 'coach.checkins' },
  { key: 'photos', icon: Camera, tk: 'coach.photos' },
];

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang, goalLabel } = useI18n();
  const [tab, setTab] = useState('master');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [noteText, setNoteText] = useState('');
  const [noteInternal, setNoteInternal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cRes, tp, np, ci, ph, notes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/training-plans/${id}`).catch(() => ({ data: null })),
        api.get(`/nutrition-plans/${id}`).catch(() => ({ data: null })),
        api.get(`/checkins/${id}`).catch(() => ({ data: [] })),
        api.get(`/photos/${id}`).catch(() => ({ data: [] })),
        api.get(`/notes/${id}`).catch(() => ({ data: [] })),
      ]);
      const cd = cRes.data || {};
      setData({ user: cd.client || cd, ob: cd.onboarding || null, sub: cd.subscription || null, tp: tp.data, np: np.data, ci: ci.data || [], ph: ph.data || [], notes: notes.data || [] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const sendNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/notes/${id}`, { text: noteText.trim(), is_internal: noteInternal });
      setNoteText('');
      toast.success('✓');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <CoachShell back="/coach/clients"><LoadingScreen /></CoachShell>;
  const u = data.user || {};
  const days = (data.tp?.weeks?.[0]?.days) || data.tp?.days || [];

  return (
    <CoachShell back="/coach/clients">
      <div className="mb-6 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.04] font-display text-lg font-extrabold text-asgym-accent">
          {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
        </div>
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-white">{u.first_name} {u.last_name}</h1>
          <p className="text-[13px] text-asgym-sub">{u.email || u.phone} · {u.goal ? goalLabel(u.goal) : '—'}</p>
        </div>
      </div>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
            className={`as-chip whitespace-nowrap ${tab === tb.key ? 'border-asgym-accent bg-asgym-accent/15 text-asgym-accent' : 'border-white/[0.08] bg-white/[0.03] text-asgym-sub'}`}>
            <tb.icon size={13} /> {t(tb.tk)}
          </button>
        ))}
      </div>

      {tab === 'master' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="as-card p-5">
            <p className="as-section mb-3">{t('coach.master')}</p>
            {[['coach.age', data.ob?.age], ['coach.height', data.ob?.height_cm && `${data.ob.height_cm}cm`], ['coach.experience', data.ob?.training_experience], ['coach.injuries', data.ob?.injuries], ['coach.allergies', data.ob?.allergies]].map(([k, v], i) => (
              <div key={i} className="flex justify-between border-b border-white/[0.05] py-2.5 last:border-0">
                <span className="as-label">{t(k)}</span><span className="text-[13px] font-bold text-white">{v || '—'}</span>
              </div>
            ))}
          </div>
          <div className="as-card p-5">
            <p className="as-section mb-3">{t('coach.weightLabel')}</p>
            {[['progress.start', u.start_weight], ['progress.current', u.current_weight], ['progress.target', u.target_weight]].map(([k, v], i) => (
              <div key={i} className="flex justify-between border-b border-white/[0.05] py-2.5 last:border-0">
                <span className="as-label">{t(k)}</span><span className="text-[13px] font-bold text-white">{v ? `${v}kg` : '—'}</span>
              </div>
            ))}
          </div>
          <div className="sm:col-span-2">
            <SubscriptionCard clientId={id} onChanged={load} t={t} />
          </div>
        </div>
      )}

      {tab === 'training' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="as-section">{t('coach.trainingPlan')} · {days.length} {t('coach.days')}</p>
            <button onClick={() => nav(`/coach/edit-training/${id}`)} className="as-btn-ghost h-10 px-4 text-xs" data-testid="edit-training-btn"><Pencil size={14} /> {t('coach.editPlans')}</button>
          </div>
          {days.length === 0 ? <p className="py-10 text-center text-sm text-asgym-sub">{t('coach.noTraining')}</p> : (
            <div className="space-y-3">
              {days.map((d, i) => (
                <div key={d.id || i} className="as-card p-4">
                  <p className="mb-2 text-sm font-bold text-white">{d.name}</p>
                  <div className="space-y-1">
                    {(d.exercises || []).map((ex, j) => (
                      <p key={j} className="text-[13px] text-asgym-sub">{ex.no || j + 1}. {ex.name} <span className="text-asgym-muted">· {(ex.sets || []).length} {t('training.sets')}</span></p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'nutrition' && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <p className="as-section">{t('coach.nutritionPlan')}</p>
            <button onClick={() => nav(`/coach/edit-nutrition/${id}`)} className="as-btn-ghost h-10 px-4 text-xs" data-testid="edit-nutrition-btn"><Pencil size={14} /> {t('coach.editPlans')}</button>
          </div>
          {!data.np || !(data.np.meals || []).length ? <p className="py-10 text-center text-sm text-asgym-sub">{t('coach.noNutrition')}</p> : (
            <>
              <div className="mb-4 flex gap-4 rounded-xl border border-white/[0.06] bg-asgym-surface p-4 text-sm">
                <span className="font-bold text-asgym-accent">{data.np.daily_calories || 0} kcal</span>
                <span className="text-asgym-sub">P {data.np.daily_protein || 0}g</span>
                <span className="text-asgym-sub">C {data.np.daily_carbs || 0}g</span>
                <span className="text-asgym-sub">F {data.np.daily_fat || 0}g</span>
              </div>
              <div className="space-y-2">
                {data.np.meals.map((m, i) => (
                  <div key={i} className="as-card p-4">
                    <div className="flex justify-between"><p className="text-sm font-bold text-white">{m.name}</p><span className="text-xs text-asgym-muted">{m.time} · {m.calories || 0} kcal</span></div>
                    {m.items && <p className="mt-1 text-[12px] text-asgym-sub">{m.items}</p>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'checkins' && (
        <div className="space-y-2.5">
          {data.ci.length === 0 ? <p className="py-10 text-center text-sm text-asgym-sub">{t('coach.noCheckins')}</p> : data.ci.map((c) => (
            <div key={c.id} className="as-card p-4">
              <div className="mb-2 flex justify-between">
                <p className="text-[13px] font-bold text-white">{new Date(c.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: 'long' })}</p>
                <span className="text-xs font-bold text-asgym-accent">{c.weight ? `${c.weight}kg` : '—'}</span>
              </div>
              <div className="flex gap-4 text-[11px] text-asgym-muted">
                <span>{t('checkin.motivation')} {c.motivation || '—'}/10</span>
                <span>{t('checkin.sleep')} {c.sleep_quality || '—'}/10</span>
                <span>{t('checkin.energy')} {c.energy_level || '—'}/10</span>
              </div>
              {c.note_to_coach && <p className="mt-2 rounded-lg bg-white/[0.03] p-2.5 text-[12px] text-asgym-sub">{c.note_to_coach}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'photos' && (
        data.ph.length === 0 ? <p className="py-10 text-center text-sm text-asgym-sub">{t('coach.noPhotos')}</p> : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {data.ph.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-xl border border-white/[0.06]">
                <img src={p.image} alt="" className="aspect-[3/4] w-full object-cover" />
                <p className="p-1.5 text-center text-[9px] text-asgym-muted">{(p.angle || '').toUpperCase()} · {new Date(p.created_at).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { day: '2-digit', month: 'short' })}</p>
              </div>
            ))}
          </div>
        )
      )}

      <div className="mt-8">
        <p className="as-section mb-3">{t('edit.notes')}</p>
        <div className="as-card mb-3 space-y-2 p-4">
          {(data.notes || []).length === 0 ? <p className="text-[13px] text-asgym-sub">—</p> : data.notes.map((n) => (
            <div key={n.id} className={`rounded-lg border p-3 ${n.is_internal ? 'border-asgym-warn/30 bg-asgym-warn/[0.06]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
              <p className="text-[13px] text-asgym-text">{n.text}</p>
              <p className="mt-1 text-[10px] font-bold tracking-wide text-asgym-muted">{n.is_internal ? t('edit.internal') : t('edit.public')}</p>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <button onClick={() => setNoteInternal((v) => !v)} className={`mb-2 as-chip ${noteInternal ? 'border-asgym-warn/40 bg-asgym-warn/10 text-asgym-warn' : 'border-asgym-accent/40 bg-asgym-accent/10 text-asgym-accent'}`}>
              {noteInternal ? t('edit.internal') : t('edit.public')}
            </button>
            <textarea className="as-input h-16 resize-none py-2.5" placeholder={t('edit.placeholder')} value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-input" />
          </div>
          <button onClick={sendNote} className="as-btn-primary h-16 w-16 shrink-0" data-testid="send-note-btn"><Send size={18} /></button>
        </div>
      </div>
    </CoachShell>
  );
}

function Line({ label, value, testid }) {
  return (
    <div className="flex justify-between border-b border-white/[0.05] py-2.5">
      <span className="as-label">{label}</span>
      <span className="text-[13px] font-bold text-white" data-testid={testid}>{value}</span>
    </div>
  );
}

function SubscriptionCard({ clientId, onChanged, t }) {
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [packages, setPackages] = useState([]);
  const [pkg, setPkg] = useState('');
  const [variant, setVariant] = useState('training');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const needsVariant = pkg === 'STANDARD';

  const refresh = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get(`/clients/${clientId}/subscription`),
        api.get('/packages'),
      ]);
      setSub(s.data.subscription || null);
      setHistory(s.data.history || []);
      setPackages(p.data || []);
      if (s.data.subscription?.package_key) {
        setPkg(s.data.subscription.package_key);
        if (s.data.subscription.variant) setVariant(s.data.subscription.variant);
      }
    } catch (e) { /* non-blocking */ }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const currentKeys = (sub?.effective_entitlements || []).map((g) => g.key);
  const targetKeys = (() => {
    const def = packages.find((x) => x.key === pkg);
    if (!def) return [];
    return def.requires_variant ? (def.variants?.find((v) => v.key === variant)?.entitlements || []) : (def.entitlements || []);
  })();
  const added = targetKeys.filter((k) => !currentKeys.includes(k));
  const removed = currentKeys.filter((k) => !targetKeys.includes(k));

  const currentLabel = sub?.grandfathered ? t('pkg.grandfathered')
    : (sub?.package_key ? `${t(`pkg.${sub.package_key}`)}${sub.variant ? ' · ' + t(`variant.${sub.variant}`) : ''}` : t('coachpkg.none'));
  const targetLabel = pkg ? `${t(`pkg.${pkg}`)}${needsVariant ? ' · ' + t(`variant.${variant}`) : ''}` : '—';
  const effStatus = sub?.effective_status || sub?.status;

  const doSave = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/subscription`, {
        package_key: pkg,
        variant: needsVariant ? variant : null,
        start_date: startDate ? `${startDate}T00:00:00+00:00` : null,
        end_date: endDate ? `${endDate}T23:59:59+00:00` : null,
      });
      toast.success(t('coachpkg.saved'));
      setConfirmOpen(false);
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="as-card p-5" data-testid="coach-subscription">
      <p className="as-section mb-3">{t('coachpkg.title')}</p>
      <Line label={t('coachpkg.package')} testid="coach-sub-package"
            value={sub?.grandfathered ? t('pkg.grandfathered') : (sub?.package_key ? t(`pkg.${sub.package_key}`) : t('coachpkg.none'))} />
      {sub?.variant && <Line label={t('coachpkg.variant')} value={t(`variant.${sub.variant}`)} />}
      <Line label={t('coachpkg.status')} testid="coach-sub-status" value={effStatus ? t(`status.${effStatus}`) : '—'} />
      <Line label={t('coachpkg.start')} value={sub?.start_date ? sub.start_date.slice(0, 10) : '—'} />
      <Line label={t('coachpkg.end')} value={sub?.end_date ? sub.end_date.slice(0, 10) : t('coachpkg.openEnded')} />
      <Line label={t('coachpkg.grandfathered')} value={sub?.grandfathered ? t('coachpkg.yes') : t('coachpkg.no')} />
      {currentKeys.length > 0 && (
        <div className="py-2.5">
          <p className="as-label mb-2">{t('coachpkg.entitlements')}</p>
          <div className="flex flex-wrap gap-1.5">
            {currentKeys.map((k) => <span key={k} className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] font-bold text-asgym-sub">{t(`ent.${k}`)}</span>)}
          </div>
        </div>
      )}
      {history.length > 0 && (
        <p className="pt-1 text-[11px] text-asgym-muted" data-testid="coach-sub-history-count">{t('coachpkg.history')}: {history.length}</p>
      )}

      <div className="mt-4 border-t border-white/[0.06] pt-4">
        <p className="as-label mb-2">{t('coachpkg.assign')}</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 sm:col-span-1">
            <p className="as-label mb-1">{t('coachpkg.package')}</p>
            <select data-testid="coach-pkg-select" value={pkg} onChange={(e) => setPkg(e.target.value)} className="as-input h-11 w-full">
              <option value="">{t('coachpkg.choosePackage')}</option>
              <option value="STANDARD">{t('pkg.STANDARD')}</option>
              <option value="PREMIUM">{t('pkg.PREMIUM')}</option>
              <option value="PRO">{t('pkg.PRO')}</option>
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="as-label mb-1">{t('coachpkg.variant')}</p>
            <select data-testid="coach-variant-select" value={needsVariant ? variant : ''} disabled={!needsVariant} onChange={(e) => setVariant(e.target.value)} className="as-input h-11 w-full disabled:opacity-40">
              {needsVariant ? (
                <>
                  <option value="training">{t('variant.training')}</option>
                  <option value="nutrition">{t('variant.nutrition')}</option>
                </>
              ) : <option value="">—</option>}
            </select>
          </div>
          <div className="col-span-1">
            <p className="as-label mb-1">{t('coachpkg.start')}</p>
            <input data-testid="coach-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="as-input h-11 w-full" />
          </div>
          <div className="col-span-1">
            <p className="as-label mb-1">{t('coachpkg.end')} <span className="text-asgym-muted">({t('coachpkg.optional')})</span></p>
            <input data-testid="coach-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="as-input h-11 w-full" />
          </div>
        </div>
        <button data-testid="coach-pkg-save" onClick={() => pkg && setConfirmOpen(true)} disabled={!pkg} className="as-btn-primary mt-3 h-11 w-full text-xs disabled:opacity-60">{t('coachpkg.save')}</button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="coach-pkg-confirm">
          <div className="as-card w-full max-w-sm p-6">
            <p className="font-display text-lg font-extrabold text-white">{t('coachpkg.confirmTitle')}</p>
            <div className="mt-4 space-y-2 text-[13px]">
              <div className="flex justify-between"><span className="as-label">{t('coachpkg.from')}</span><span className="font-bold text-white">{currentLabel}</span></div>
              <div className="flex justify-between"><span className="as-label">{t('coachpkg.to')}</span><span className="font-bold text-asgym-accent">{targetLabel}</span></div>
            </div>
            {(added.length > 0 || removed.length > 0) && (
              <div className="mt-4 space-y-3">
                {added.length > 0 && <div><p className="as-label mb-1 !text-emerald-400">{t('coachpkg.gains')}</p><div className="flex flex-wrap gap-1.5">{added.map((k) => <span key={k} className="rounded-lg bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-300">{t(`ent.${k}`)}</span>)}</div></div>}
                {removed.length > 0 && <div><p className="as-label mb-1 !text-red-400">{t('coachpkg.losses')}</p><div className="flex flex-wrap gap-1.5">{removed.map((k) => <span key={k} className="rounded-lg bg-red-500/10 px-2 py-0.5 text-[11px] font-bold text-red-300 line-through">{t(`ent.${k}`)}</span>)}</div></div>}
              </div>
            )}
            <p className="mt-4 text-[12px] leading-relaxed text-asgym-sub">{t('coachpkg.noDataLoss')}</p>
            <div className="mt-5 flex gap-2">
              <button data-testid="coach-pkg-cancel" onClick={() => setConfirmOpen(false)} className="as-btn-ghost h-11 flex-1 text-xs">{t('common.cancel')}</button>
              <button data-testid="coach-pkg-confirm-btn" onClick={doSave} disabled={saving} className="as-btn-primary h-11 flex-1 text-xs disabled:opacity-60">{t('coachpkg.confirmSave')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
